import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Card, Input, Typography, Row, Col, Button, message, Select, Switch } from 'antd';
import { RightOutlined, CaretDownOutlined } from '@ant-design/icons';
import { updateNavigationTourGuide } from 'store/user';
import Alert from 'components/Alert';
import merchantAPI from 'services/merchants';
import { CARESPOC } from './const';

const SettingsCardWrapper = styled(Card)`
  margin: 8px 0px 8px 0px;
`;

const SettingsInputWrapper = styled(Input)`
  padding: 8px;
  .ant-input .ant-input {
    background-color: #f4f4f4;
  }
  .ant-input-disabled {
    background-color: #f4f4f4;
  }
  .ant-input-disabled .ant-input-wrap .ant-input {
    color: #f4f4f4;
  }
`;

const SettingsSelectWrapper = styled.div`
  .ant-select {
    display: grid;
  }
  .ant-select .ant-select-selector {
    min-height: 40px;
    align-items: center;
  }
`;

function GeneralSettings({ userinfo }) {
  const partnerId = userinfo?.merchant_id;
  const [careSpoc, setCareSpoc] = useState('');
  const dispatch = useDispatch();

  // onchange value
  const handleSpoc = (value) => {
    setCareSpoc(value);
  };

  // kyc redirection for partner
  const kycVerify = async (merchantId) => {
    message.info({
      content: 'Fetching Kyc verification link'
    });

    const response = await merchantAPI.kycVerify({ merchant_id: merchantId });

    if (response.success) {
      window.open(response.data.redirection_url);
      message.info({
        content: 'You are being redirected to link'
      });
    } else {
      console.log('something went wrong');
      Alert('something went wrong', 'Please contact support team for this', 'error');
    }
  };

  // Api calling on save details
  const updateSpoc = async () => {
    const response = await merchantAPI.updateCareSpoc({ care_spoc: careSpoc });

    if (response.success) {
      message.success({
        content: 'Updated'
      });
    } else {
      console.log('something went wrong');
      Alert('something went wrong', 'Please contact support team for this', 'error');
    }
  };

  const handleGuideChange = async (paramKey) => {
    const request = {
      tour_guide: paramKey
    };
    const response = await merchantAPI.fetchNavigationFlag(request);
    if (response.success) {
      dispatch(updateNavigationTourGuide(response?.data));
    } else {
      console.log('something went wrong');
    }
  };

  const fetchSettingsConfig = async () => {
    try {
      const response = await merchantAPI.fetchSettingsConfig({});
      if (response.success) {
        setCareSpoc(response?.data?.settings_config?.care_spoc);
      }
    } catch (error) {
      Alert('Something went wrong', 'Please contact support team for this.', 'error');
    }
  };

  useEffect(() => {
    fetchSettingsConfig();
  }, [partnerId]);

  const canUpdateKyc = userinfo?.configuration?.permission_configs?.can_update_kyc;
  return (
    <>
      <SettingsCardWrapper>
        <Row>
          <Col span={24} style={{ marginTop: '-25px' }}>
            <Typography.Title level={4}>Profile Details</Typography.Title>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={7}>
            Name
            <SettingsInputWrapper defaultValue={userinfo?.name} disabled />
          </Col>
          <Col span={7}>
            Email ID
            <SettingsInputWrapper defaultValue={userinfo?.email} disabled />
          </Col>
          <Col span={7}>
            Phone
            <SettingsInputWrapper defaultValue={userinfo?.mobile_number} disabled />
          </Col>
        </Row>
        <Row justify="end" style={{ marginTop: '30px' }}>
          <Col xs={{ span: 4 }} lg={{ span: 4 }} md={{ span: 4 }} style={{ display: 'contents' }}>
            <Button
              size="large"
              style={{
                boxShadow: '0px 3px 6px #0000000F',
                backgroundColor: 'var(--color-fill-alter)',
                color: '#000000',
                borderColor: '#EFEAFF',
                marginRight: '50px'
              }}
              onClick={() => window.open(process.env.REACT_APP_AUTH_RESET_PASSWORD_URL)}
            >
              Reset Password
            </Button>
            <Button
              type="primary"
              size="large"
              style={{
                boxShadow: '0px 3px 6px #0000000f'
              }}
              onClick={() => {
                kycVerify();
              }}
            >
              Edit Profile <RightOutlined />
            </Button>
          </Col>
        </Row>
      </SettingsCardWrapper>
      <SettingsCardWrapper>
        <Row>
          <Col span={24} style={{ marginTop: '-25px' }}>
            <Typography.Title level={4}>SPOC Details</Typography.Title>
          </Col>
        </Row>
        <Row justify="start">
          <Col span={7} style={{ margin: '0px 90px 0px 0px' }}>
            KYC SPOC
            <SettingsInputWrapper defaultValue={canUpdateKyc ? 'Partner' : 'Merchant'} disabled />
          </Col>
          <Col span={7}>
            Care SPOC
            <SettingsSelectWrapper>
              <Select
                suffixIcon={<CaretDownOutlined style={{ fontSize: '15px', color: '#A0A0A0' }} />}
                value={careSpoc || undefined}
                options={CARESPOC}
                onChange={handleSpoc}
              />
            </SettingsSelectWrapper>
          </Col>
        </Row>
        <Row justify="end" style={{ marginTop: '2px' }}>
          <Col xs={{ span: 4 }} lg={{ span: 4 }} md={{ span: 4 }} style={{ display: 'contents' }}>
            <Button
              type="primary"
              size="large"
              style={{
                boxShadow: '0px 3px 6px #0000000f'
              }}
              onClick={() => {
                updateSpoc();
              }}
            >
              Save Details
            </Button>
          </Col>
        </Row>
      </SettingsCardWrapper>
      <SettingsCardWrapper>
        <Row>
          <Col span={24} style={{ marginTop: '-25px' }}>
            <Typography.Title level={4} style={{ fontWeight: '700px', fontSize: '18px' }}>
              User Guide
            </Typography.Title>
          </Col>
        </Row>
        <Row justify="start" style={{ marginTop: '12px' }}>
          <Col
            span={4}
            style={{
              display: 'flex',
              gap: '18px',
              flexDirection: 'row'
            }}
          >
            <Typography.Text style={{ fontSize: '16px' }}>Enable</Typography.Text>
            <Switch onChange={handleGuideChange} />
          </Col>
        </Row>
      </SettingsCardWrapper>
    </>
  );
}

GeneralSettings.propTypes = {
  userinfo: PropTypes.instanceOf(Object).isRequired
};

export default GeneralSettings;
